import {
  ArrowBack as ArrowBackIcon,
  Print as PrintIcon,
  Search as SearchIcon,
} from '../components/icons';
import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  Chip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import orderService from '../services/orderService';
import { getErrorMessage } from '../utils/errorUtils';
import { getHoverColor, hexToRgba } from '../utils/colorUtils';
import { printReceipt } from '../utils/receiptUtils';
import { usePublicTheme } from '../contexts/PublicThemeContext';
import { useCurrencyFormatter } from '../contexts/CurrencyContext';
import { formatDateTime as formatDateTimeUtil } from '../utils/dateUtils';

const TrackOrderPage = () => {
  const navigate = useNavigate();
  const { primaryColor, updateMerchantTheme } = usePublicTheme();
  const { formatCurrency } = useCurrencyFormatter();
  const [orderNumber, setOrderNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [order, setOrder] = useState(null);

  // Update merchant theme when order data is available
  useEffect(() => {
    if (order?.merchant?.settings) {
      updateMerchantTheme(order.merchant.settings);
    }
  }, [order?.merchant?.settings, updateMerchantTheme]);

  const handleTrackOrder = async () => {
    if (!orderNumber.trim()) {
      setError('Please enter your order number');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Validate that the order exists before navigating
      const orderData = await orderService.getPublicOrderByNumber(orderNumber.trim());

      if (orderData && orderData.order_number) {
        setOrder(orderData);
        setError('');
      } else {
        setError('Order not found. Please check your order number and try again.');
      }
    } catch (error) {
      console.error('Error tracking order:', error);
      if (error.response && error.response.status === 404) {
        setError('Order not found. Please check your order number and try again.');
      } else {
        const errorMessage = error.userMessage || getErrorMessage(error, 'Unable to track order. Please try again later.');
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleTrackOrder();
    }
  };

  const handleGoBack = () => {
    if (order) {
      // If we have order data, go back to input mode
      setOrder(null);
      setOrderNumber('');
      setError('');
    } else {
      navigate(-1);
    }
  };

  const handleGoToMenu = () => {
    navigate('/menu');
  };

  const handlePrintReceipt = () => {
    if (order) {
      printReceipt(order, order.merchant, formatCurrency);
    }
  };

  const formatDateTime = (dateString) => formatDateTimeUtil(dateString);

  // If we have order data, show order details
  if (order) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', py: 4 }}>
        <Container maxWidth="md">
          {/* Header with back button */}
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={handleGoBack}
              sx={{ mr: 2 }}
              aria-label="go back"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              Order Details
            </Typography>
          </Box>

          {/* Order Status Card */}
          <Paper elevation={3} sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: primaryColor }}>
                  Order #{order.order_number}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Placed on {formatDateTime(order.created_at)}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {order.merchant?.name}
                </Typography>
              </Box>

              {/* Order Items */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Order Items
                </Typography>
                {order.items && order.items.map((item, index) => (
                  <Box key={index} sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1,
                    borderBottom: index < order.items.length - 1 ? '1px solid #eee' : 'none'
                  }}>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {item.quantity}x {item.menu_item?.name || item.name}
                      </Typography>
                      {item.options && item.options.length > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2, fontSize: '0.8rem' }}>
                          Base {formatCurrency(Number(item.price) || 0)}
                          {item.options.map((opt) =>
                            ` · ${opt.name}${Number(opt.price_addition) > 0 ? ` +${formatCurrency(Number(opt.price_addition))}` : ''}`
                          ).join('')}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatCurrency(parseFloat(item.total))}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Order Total */}
              <Box sx={{
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                p: 2,
                mb: 3
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatCurrency(order.subtotal)}
                  </Typography>
                </Box>
                {order.tax > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Tax</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(order.tax)}
                    </Typography>
                  </Box>
                )}
                {order.discount_details && order.discount_details.length > 0 ? (
                  order.discount_details.map((disc) => (
                    <Box key={disc.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">{disc.name}</Typography>
                        {disc.is_auto && (
                          <Chip
                            label="Auto"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              bgcolor: '#dbeafe',
                              color: '#1d4ed8',
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#16a34a' }}>
                        -{formatCurrency(Number(disc.amount))}
                      </Typography>
                    </Box>
                  ))
                ) : order.discount > 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Discount</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#16a34a' }}>
                      -{formatCurrency(order.discount)}
                    </Typography>
                  </Box>
                ) : null}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Total</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: primaryColor }}>
                    {formatCurrency(order.total)}
                  </Typography>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<PrintIcon />}
                  onClick={handlePrintReceipt}
                  sx={{
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${getHoverColor(primaryColor, 'darken', 15)} 100%)`,
                    boxShadow: `0 4px 12px ${hexToRgba(primaryColor, 0.3)}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${getHoverColor(primaryColor, 'darken', 10)} 0%, ${getHoverColor(primaryColor, 'darken', 20)} 100%)`,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 16px ${hexToRgba(primaryColor, 0.4)}`,
                    },
                    fontWeight: 600
                  }}
                >
                  PRINT RECEIPT
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleGoToMenu}
                  sx={{
                    borderColor: primaryColor,
                    color: primaryColor,
                    '&:hover': {
                      borderColor: getHoverColor(primaryColor, 'darken', 15),
                      bgcolor: getHoverColor(primaryColor, 'lighten', 90),
                      color: getHoverColor(primaryColor, 'darken', 15)
                    },
                    fontWeight: 600
                  }}
                >
                  GO TO MENU
                </Button>
              </Box>
            </CardContent>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', py: 4 }}>
      <Container maxWidth="sm">
        {/* Header with back button */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={handleGoBack}
            sx={{ mr: 2 }}
            aria-label="go back"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Track Your Order
          </Typography>
        </Box>

        {/* Main tracking card */}
        <Paper elevation={3} sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <SearchIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Enter Your Order Number
              </Typography>
              <Typography variant="body1" color="text.secondary">
                You can find your order number in your order confirmation
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <TextField
                autoFocus
                fullWidth
                label="Order Number"
                placeholder="e.g., TES123ABC"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
                onKeyPress={handleKeyPress}
                disabled={loading}
                variant="outlined"
                size="large"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1.1rem',
                    fontFamily: 'monospace'
                  }
                }}
                helperText="Order numbers are case-insensitive"
              />
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleTrackOrder}
              disabled={loading || !orderNumber.trim()}
              sx={{
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${getHoverColor(primaryColor, 'darken', 15)} 100%)`,
                boxShadow: `0 8px 25px ${hexToRgba(primaryColor, 0.35)}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: `linear-gradient(135deg, ${getHoverColor(primaryColor, 'darken', 10)} 0%, ${getHoverColor(primaryColor, 'darken', 20)} 100%)`,
                  transform: 'translateY(-3px)',
                  boxShadow: `0 12px 35px ${hexToRgba(primaryColor, 0.45)}`,
                },
                '&:disabled': {
                  background: '#ccc',
                  color: '#666',
                }
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 2 }} />
                  Tracking Order...
                </>
              ) : (
                <>
                  <SearchIcon sx={{ mr: 1 }} />
                  Track Order
                </>
              )}
            </Button>
          </CardContent>
        </Paper>

        {/* Help section */}
        <Card sx={{ mt: 3, backgroundColor: '#f8f9fa' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Need Help?
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              • Order numbers are typically 6-9 characters long
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              • Check your email confirmation or receipt
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Contact the restaurant if you can't find your order number
            </Typography>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default TrackOrderPage;
